import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import { NewsLetterBoxType } from './Footer';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Form, FormText, Label } from 'reactstrap';
import { LoggerFactory } from '@ibe/services';
import { Element as ScrollTo } from 'react-scroll';
import { CustomInput, ScrollerService } from '@ibe/components';

const schema = yup
  .object()
  .shape({
    newsletterEmail: yup.string().email().required()
  })
  .defined();

const newsletterEmail = 'newsletterEmail';
const newsletterConsent = 'newsletterConsent';
const consentErrorScroll = 'consentErrorScroll';
const newsletterErrorScroll = 'newsletterErrorScroll';

export const validateNewsLetterForm = async (values: Schema): Promise<SchemaErrors> => {
  const errors: { [key: string]: string[] } = {};
  await schema.validate(values, { abortEarly: false }).catch((e: yup.ValidationError) => {
    return e.inner.forEach(ee => {
      if (ee && ee.path) {
        errors[ee.path] = ee.errors;
      }
    });
  });
  return errors as SchemaErrors;
};

type Schema = yup.InferType<typeof schema>;
type SchemaErrors = Partial<Record<keyof Schema, string[]>>;

const NewsLetterInputRenderer = (props: NewsLetterBoxType) => {
  const logger = LoggerFactory.get('NewsLetterInput');

  const { className: buttonClassName, buttonText = '', disableIfNotValid } = props?.submit || {};
  const { active: validationActive = true, mode } = props?.validation || {};
  const {
    defaultValue = '',
    label,
    className: inputClassName,
    onTextChange,
    onFocus,
    placeholder = ''
  } = props?.input || {};
  const withConsent = props.consent;
  const {
    defaultValue: consentDefaultVal,
    consentLabel,
    mustBeChecked = true
  } = props.consent || {};

  const { register, handleSubmit, formState, getValues } = useForm<Schema>({
    resolver: validationActive ? yupResolver(schema) : undefined,
    defaultValues: { [newsletterEmail]: defaultValue },
    mode: mode ? mode : 'onSubmit'
  });
  const { errors } = formState;
  const log = () => logger.log('Newsletter Form Submitted');
  const [checked, setChecked] = useState(!!consentDefaultVal);
  const [consentError, setConsentError] = useState(false);

  const checkValidation = () => {
    if (mustBeChecked) {
      if (checked && formState.isValid) return true;
      return false;
    }
    return formState.isValid;
  };

  const toggleCheckBox = () => {
    setChecked(!checked);
    setConsentError(false);
  };
  const isValid = checkValidation();

  useEffect(() => {
    if (onTextChange) {
      onTextChange(getValues().newsletterEmail, isValid);
    }
  }, [onTextChange, formState, getValues]);

  const hasError = Object.prototype.hasOwnProperty.call(errors, newsletterEmail);

  const onSubmit = (values: { [a: string]: string }) => {
    if (withConsent) {
      if (!checked) {
        ScrollerService.scrollTo(consentErrorScroll);
        return setConsentError(true);
      }
    }
    validateNewsLetterForm(getValues()).then(newErrors => {
      if (Object.keys(newErrors).length === 0) {
        if (props.submit?.onSubmit) props.submit?.onSubmit(values);
        log();
      }
      ScrollerService.scrollTo(newsletterErrorScroll);
    });
  };
  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="iso__footer__newsletter__input__button">
          <div>
            {label ? <Label htmlFor={newsletterEmail}>{label}</Label> : null}
            <input
              className={`form-control ${hasError ? 'form-control--error' : ''} ${inputClassName}`}
              id={newsletterEmail}
              placeholder={placeholder}
              onFocus={onFocus}
              {...register(newsletterEmail)}
            />
          </div>
          <Button
            className={buttonClassName}
            disabled={disableIfNotValid && !isValid}
            type="submit"
          >
            {buttonText}
          </Button>
        </div>
        <ScrollTo name={newsletterErrorScroll}>
          <div>
            {hasError && (
              <FormText color="danger">
                {props.input?.errorMessage || `${errors?.[newsletterEmail]}`}
              </FormText>
            )}
          </div>
        </ScrollTo>
      </Form>
      <div>
        {withConsent ? (
          <CustomInput
            className={`iso__footer-consent-input ${
              consentError ? 'iso__footer-consent-input--error' : ''
            }`}
            type="checkbox"
            id={newsletterConsent}
            checked={checked}
            onChange={toggleCheckBox}
            label={consentLabel}
          />
        ) : null}
        <ScrollTo name={consentErrorScroll}>
          <div>
            {consentError && <FormText color="danger">{props.consent?.errorMessage}</FormText>}
          </div>
        </ScrollTo>
      </div>
    </>
  );
};

export default NewsLetterInputRenderer;
